import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Upload, Save, Image as ImageIcon } from 'lucide-react';
import { api, fileUrl } from '../lib/api';

export default function AboutEditor() {
  const [content, setContent] = useState(null);
  const [draft, setDraft] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    const { data } = await api.get('/about');
    setContent(data);
    setDraft({
      heading: data.heading || '',
      intro_label: data.intro_label || '',
      paragraphs: data.paragraphs?.length ? data.paragraphs : [''],
      info_blocks: data.info_blocks?.length ? data.info_blocks : [{ label: '', value: '' }],
      cta_primary_text: data.cta_primary_text || '',
      cta_primary_link: data.cta_primary_link || '',
      cta_secondary_text: data.cta_secondary_text || '',
      cta_secondary_link: data.cta_secondary_link || '',
    });
  };

  useEffect(() => { load(); }, []);

  if (!draft) {
    return <div className="font-osd text-[#8b6a3e] text-sm">// LOADING ABOUT CONTENT…</div>;
  }

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setParagraph = (i, v) => setDraft((d) => ({ ...d, paragraphs: d.paragraphs.map((p, idx) => idx === i ? v : p) }));
  const addParagraph = () => setDraft((d) => ({ ...d, paragraphs: [...d.paragraphs, ''] }));
  const removeParagraph = (i) => setDraft((d) => ({ ...d, paragraphs: d.paragraphs.filter((_, idx) => idx !== i) }));
  const setInfo = (i, field, v) => setDraft((d) => ({ ...d, info_blocks: d.info_blocks.map((b, idx) => idx === i ? { ...b, [field]: v } : b) }));
  const addInfo = () => setDraft((d) => ({ ...d, info_blocks: [...d.info_blocks, { label: '', value: '' }] }));
  const removeInfo = (i) => setDraft((d) => ({ ...d, info_blocks: d.info_blocks.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload = {
        ...draft,
        paragraphs: draft.paragraphs.filter((p) => p.trim().length > 0),
        info_blocks: draft.info_blocks.filter((b) => b.label.trim() || b.value.trim()),
      };
      await api.patch('/admin/about', payload);
      setMsg({ type: 'ok', text: 'About page saved.' });
      await load();
    } catch (e) {
      setMsg({ type: 'err', text: e?.response?.data?.detail || e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) { setMsg({ type: 'err', text: 'Choose a photo first.' }); return; }
    setPhotoUploading(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', photoFile);
      await api.post('/admin/about/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoFile(null);
      setMsg({ type: 'ok', text: 'Photo updated.' });
      await load();
    } catch (e) {
      setMsg({ type: 'err', text: e?.response?.data?.detail || 'Upload failed' });
    } finally {
      setPhotoUploading(false);
    }
  };

  const currentPhoto = content?.photo_filename ? fileUrl(content.photo_filename) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6 [&>*]:min-w-0" data-testid="admin-about-editor">

      {/* Photo column */}
      <div className="border-2 border-[#3a2512] bg-[#0f0704] p-5 rounded-lg space-y-4">
        <div className="font-osd text-[#f5a623] text-sm tracking-widest">// STUDIO PORTRAIT</div>

        <div className="aspect-[4/5] w-full max-w-[16rem] mx-auto lg:max-w-none rounded border border-[#3a2512] bg-[#1a0e05] overflow-hidden flex items-center justify-center">
          {currentPhoto ? (
            <img src={currentPhoto} alt="Owen portrait" className="w-full h-full object-cover" />
          ) : (
            <div className="text-[#8b6a3e] font-osd text-xs flex flex-col items-center gap-2 p-6 text-center">
              <ImageIcon className="w-10 h-10" />
              USING DEFAULT STOCK PHOTO. UPLOAD ONE TO REPLACE.
            </div>
          )}
        </div>

        <label className="block">
          <span className="font-osd text-xs text-[#a88552] tracking-widest">CHOOSE NEW PHOTO (.jpg / .png / .webp)</span>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
            data-testid="about-photo-file"
            className="mt-1 w-full font-osd text-xs text-[#d8b887] file:mr-3 file:py-2 file:px-3 file:border file:border-[#5a3418] file:bg-[#1a0e05] file:text-[#f5a623] file:font-osd file:text-xs file:cursor-pointer file:rounded"
          />
        </label>

        <button
          onClick={uploadPhoto}
          disabled={!photoFile || photoUploading}
          data-testid="about-photo-upload-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#f5a623] text-[#1a0e05] font-osd tracking-widest px-4 py-2.5 rounded border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05] hover:bg-[#ffc968] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Upload className="w-4 h-4" /> {photoUploading ? 'TRANSMITTING…' : 'REPLACE PHOTO'}
        </button>
      </div>

      {/* Text column */}
      <div className="border-2 border-[#3a2512] bg-[#0f0704] p-5 rounded-lg space-y-5">
        <div className="font-osd text-[#f5a623] text-sm tracking-widest">// PAGE CONTENT</div>

        <Field label="HEADING">
          <input
            type="text"
            value={draft.heading}
            onChange={(e) => setField('heading', e.target.value)}
            data-testid="about-heading"
            className="adm-input"
          />
        </Field>

        <Field label="PHOTO BADGE LABEL">
          <input
            type="text"
            value={draft.intro_label}
            onChange={(e) => setField('intro_label', e.target.value)}
            data-testid="about-intro-label"
            className="adm-input"
          />
        </Field>

        <div>
          <div className="font-osd text-xs text-[#a88552] tracking-widest mb-2 flex items-center justify-between">
            PARAGRAPHS
            <button onClick={addParagraph} className="adm-mini-btn admin-action" data-testid="about-add-paragraph">
              <Plus className="w-3 h-3" /> ADD
            </button>
          </div>
          <div className="space-y-2">
            {draft.paragraphs.map((p, i) => (
              <div key={i} className="flex gap-2 items-start">
                <textarea
                  value={p}
                  onChange={(e) => setParagraph(i, e.target.value)}
                  rows={3}
                  data-testid={`about-paragraph-${i}`}
                  className="adm-input min-w-0 resize-y flex-1"
                  placeholder={`Paragraph ${i + 1}…`}
                />
                <button onClick={() => removeParagraph(i)} className="adm-icon-btn admin-action shrink-0" aria-label={`Remove paragraph ${i + 1}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="font-osd text-xs text-[#a88552] tracking-widest mb-2 flex items-center justify-between">
            INFO BLOCKS (label · value)
            <button onClick={addInfo} className="adm-mini-btn admin-action" data-testid="about-add-info">
              <Plus className="w-3 h-3" /> ADD
            </button>
          </div>
          <div className="space-y-2">
            {draft.info_blocks.map((b, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto] sm:grid-cols-[8rem_1fr_auto] gap-2 items-start">
                <input
                  type="text"
                  value={b.label}
                  onChange={(e) => setInfo(i, 'label', e.target.value)}
                  className="adm-input min-w-0 uppercase"
                  placeholder="LABEL"
                  aria-label={`Info block ${i + 1} label`}
                  data-testid={`about-info-label-${i}`}
                />
                <input
                  type="text"
                  value={b.value}
                  onChange={(e) => setInfo(i, 'value', e.target.value)}
                  className="adm-input min-w-0 col-span-2 sm:col-span-1 order-3 sm:order-none"
                  placeholder="Value…"
                  aria-label={`Info block ${i + 1} value`}
                  data-testid={`about-info-value-${i}`}
                />
                <button onClick={() => removeInfo(i)} className="adm-icon-btn admin-action shrink-0 order-2 sm:order-none" aria-label={`Remove info block ${i + 1}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="PRIMARY BUTTON TEXT">
            <input type="text" value={draft.cta_primary_text} onChange={(e) => setField('cta_primary_text', e.target.value)} className="adm-input" />
          </Field>
          <Field label="PRIMARY BUTTON LINK">
            <input type="text" value={draft.cta_primary_link} onChange={(e) => setField('cta_primary_link', e.target.value)} className="adm-input" placeholder="/animation or https://…" />
          </Field>
          <Field label="SECONDARY BUTTON TEXT">
            <input type="text" value={draft.cta_secondary_text} onChange={(e) => setField('cta_secondary_text', e.target.value)} className="adm-input" />
          </Field>
          <Field label="SECONDARY BUTTON LINK">
            <input type="text" value={draft.cta_secondary_link} onChange={(e) => setField('cta_secondary_link', e.target.value)} className="adm-input" placeholder="mailto:you@…" />
          </Field>
        </div>

        {msg && (
          <div
            className={`font-osd text-xs px-3 py-2 rounded border ${msg.type === 'ok' ? 'text-[#9dd68a] border-[#9dd68a]/50 bg-[#0e1a09]' : 'text-[#ff6a3a] border-[#ff6a3a]/50 bg-[#1a0e05]'}`}
            data-testid="about-save-status"
          >
            {msg.text}
          </div>
        )}

        <button
          onClick={save}
          disabled={saving}
          data-testid="about-save-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#f5a623] text-[#1a0e05] font-osd tracking-widest px-5 py-3 rounded border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05] hover:bg-[#ffc968] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" /> {saving ? 'SAVING…' : 'SAVE CHANGES'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-osd text-xs text-[#a88552] tracking-widest">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
